import styled from 'styled-components'

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  scroll-margin-top: 24px;
  border-radius: ${({ theme }) => theme.radius.control};
  background: ${({ theme }) => theme.base.white};
  box-shadow: ${({ theme }) => `inset 0 0 0 1px ${theme.grey[350]}, ${theme.shadows.control}`};
`

export const SectionHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 16px 20px 0;

  @media (max-width: 768px) {
    padding: 12px 12px 0;
  }
`

export const SectionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  margin-right: auto;
`

export const SectionTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.palette[900]};
  font-size: 18px;
  line-height: 24px;
  font-weight: 500;
`

export const Description = styled.span`
  color: ${({ theme }) => theme.grey[600]};
  font-size: 14px;
  line-height: 20px;
`

export const TabsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  padding: 12px 20px 0;
  border-bottom: ${({ theme }) => `1px solid ${theme.grey[350]}`};

  @media (max-width: 768px) {
    padding: 12px 12px 0;
  }
`

export const Tabs = styled.div`
  display: flex;
  margin-right: auto;
`

export const Tab = styled.button<{ $active?: boolean }>`
  appearance: none;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: -1px;
  padding: 10px 16px;
  border: 0;
  border-bottom: 2px solid ${({ theme, $active }) => $active ? theme.accent[100] : 'transparent'};
  background: transparent;
  color: ${({ theme, $active }) => $active ? theme.accent[100] : theme.grey[700]};
  font: inherit;
  font-size: 15px;
  line-height: 20px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: ${({ theme, $active }) => $active ? theme.accent[100] : theme.palette[900]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.isDark ? theme.shadows.focusDark : theme.shadows.focus};
  }
`

export const TabCount = styled.span<{ $active?: boolean }>`
  padding: 1px 6px;
  border-radius: 10px;
  background: ${({ theme, $active }) => $active ? theme.accent[15] : theme.grey[300]};
  font-size: 12px;
  line-height: 16px;
`

export const TabsNote = styled.span`
  padding-bottom: 12px;
  color: ${({ theme }) => theme.grey[700]};
  font-size: 13px;
  line-height: 18px;
`

export const Rows = styled.div`
  display: flex;
  flex-direction: column;
`

export const Row = styled.button<{ $active?: boolean }>`
  appearance: none;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  width: 100%;
  padding: 14px 20px;
  box-sizing: border-box;
  border: 0;
  border-bottom: ${({ theme }) => `1px solid ${theme.grey[300]}`};
  background: ${({ theme, $active }) => $active ? theme.primary[25] : 'transparent'};
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.grey[100]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => `inset 0 0 0 2px ${theme.accent[100]}`};
  }

  @media (max-width: 768px) {
    padding: 14px 12px;
  }
`

export const Cell = styled.div<{ $basis: string }>`
  display: flex;
  flex: ${({ $basis }) => `1 1 ${$basis}`};
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

export const Name = styled.span`
  color: ${({ theme }) => theme.palette[900]};
  font-size: 15px;
  line-height: 20px;
  font-weight: 500;
  overflow-wrap: anywhere;
`

export const Text = styled.span`
  color: ${({ theme }) => theme.palette[900]};
  font-size: 14px;
  line-height: 20px;
  overflow-wrap: anywhere;
`

export const Muted = styled.span`
  color: ${({ theme }) => theme.grey[600]};
  font-size: 13px;
  line-height: 18px;
  overflow-wrap: anywhere;
`

export const Steps = styled.ol`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
`

export const Step = styled.li<{ $dot: string; $halo: string; $color: string; $line?: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ $color }) => $color};
  font-size: 13px;
  line-height: 18px;
  white-space: nowrap;

  &::before {
    content: '';
    flex: none;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ $dot }) => $dot};
    box-shadow: 0 0 0 3px ${({ $halo }) => $halo};
  }

  &::after {
    content: '';
    display: ${({ $line }) => $line ? 'block' : 'none'};
    width: 18px;
    height: 2px;
    margin: 0 2px;
    background: ${({ $line }) => $line || 'transparent'};
  }
`

export const Date = styled.span`
  flex: 0 0 auto;
  min-width: 110px;
  color: ${({ theme }) => theme.grey[600]};
  font-size: 13px;
  line-height: 18px;
  text-align: right;

  @media (max-width: 768px) {
    min-width: 0;
    text-align: left;
  }
`

export const Chevron = styled.span`
  display: flex;
  color: ${({ theme }) => theme.grey[700]};

  img, svg {
    width: 16px;
    height: 16px;
    transform: rotate(-90deg);
  }
`

export const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 20px;
  color: ${({ theme }) => theme.grey[600]};
  font-size: 14px;
  line-height: 20px;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

export const Empty = styled.div`
  padding: 24px 20px;
  color: ${({ theme }) => theme.grey[600]};
  font-size: 15px;
  line-height: 20px;
`

export const Alert = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin: 12px 20px 0;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.control};
  background: ${({ theme }) => theme.errors[100]};
  color: ${({ theme }) => theme.errors[600]};
  font-size: 14px;
  line-height: 20px;
`

export const Overlay = styled.div<{ $z?: number }>`
  position: fixed;
  inset: 0;
  z-index: ${({ $z }) => $z ?? 1000};
  background: rgba(30, 30, 30, 0.35);
`

export const Drawer = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 440px;
  background: ${({ theme }) => theme.base.white};
  box-shadow: ${({ theme }) => theme.shadows.menu};
`

export const DrawerHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 20px 20px 16px;
  border-bottom: ${({ theme }) => `1px solid ${theme.grey[350]}`};
`

export const DrawerHeading = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

export const Eyebrow = styled.span`
  color: ${({ theme }) => theme.grey[600]};
  font-size: 12px;
  line-height: 1;
  text-transform: uppercase;
`

export const DrawerTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.palette[900]};
  font-size: 20px;
  line-height: 26px;
  font-weight: 500;
  overflow-wrap: anywhere;
`

export const Pill = styled.span<{ $bg: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 3px 8px;
  border-radius: ${({ theme }) => theme.radius.xs};
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
  }
`

export const CloseButton = styled.button`
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.control};
  background: transparent;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.palette[200]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.isDark ? theme.shadows.focusDark : theme.shadows.focus};
  }
`

export const DrawerBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  padding: 20px;
`

export const Timeline = styled.ol`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
`

export const TimelineItem = styled.li<{ $dot: string; $halo: string; $line?: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 0 14px 28px;

  &::before {
    content: '';
    position: absolute;
    top: 4px;
    left: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${({ $dot }) => $dot};
    box-shadow: 0 0 0 3px ${({ $halo }) => $halo};
  }

  &::after {
    content: '';
    display: ${({ $line }) => $line ? 'block' : 'none'};
    position: absolute;
    top: 20px;
    bottom: 0;
    left: 7px;
    width: 2px;
    background: ${({ $line }) => $line || 'transparent'};
  }
`

export const TimelineLabel = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-size: 15px;
  line-height: 20px;
  font-weight: 500;
`

export const Facts = styled.dl`
  display: flex;
  flex-direction: column;
  margin: 0;
  border-radius: ${({ theme }) => theme.radius.control};
  box-shadow: ${({ theme }) => `inset 0 0 0 1px ${theme.grey[350]}`};
`

export const Fact = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px 14px;
  border-bottom: ${({ theme }) => `1px solid ${theme.grey[300]}`};

  &:last-child {
    border-bottom: 0;
  }

  dt {
    color: ${({ theme }) => theme.grey[600]};
    font-size: 12px;
    line-height: 1;
    text-transform: uppercase;
  }

  dd {
    margin: 0;
    color: ${({ theme }) => theme.palette[900]};
    font-size: 15px;
    line-height: 20px;
    overflow-wrap: anywhere;
  }
`

export const DrawerFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 20px;
  border-top: ${({ theme }) => `1px solid ${theme.grey[350]}`};
`

export const Confirm = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 1003;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: calc(100% - 32px);
  max-width: 400px;
  padding: 24px;
  box-sizing: border-box;
  transform: translate(-50%, -50%);
  border-radius: ${({ theme }) => theme.radius.card};
  background: ${({ theme }) => theme.base.white};
  box-shadow: ${({ theme }) => theme.shadows.menu};
  text-align: center;
`

export const ConfirmIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${({ theme }) => theme.errors[100]};

  img, svg {
    width: 28px;
    height: 28px;
  }
`

export const ConfirmTitle = styled.span`
  color: ${({ theme }) => theme.palette[900]};
  font-size: 18px;
  line-height: 24px;
  font-weight: 500;
  overflow-wrap: anywhere;
`

export const ConfirmActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  width: 100%;
  margin-top: 4px;

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`
